import React, { useState, useMemo, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getEuLegalBasisDescription } from './../data/shared/table/format.js';

// A colour palette for the charts using CSS variables for better theming
const COLOURS = [
    '#0088FE',        // Blue
    '#00C49F',                     // Teal
    '#FFBB28',                     // Yellow
    '#FF8042',                     // Orange
    '#AF19FF',                     // Purple
    '#775DD0',                     // Indigo
    '#82ca9d',                     // Light Green
    '#ff5858ff',                     // Gold
    '#a4de6c',                     // Lime Green
    '#d0ed57',                     // Pale Yellow
    '#ffc658'                      // Fallback/repeat
];

/**
 * Custom Tooltip for Charts
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const displayLabel = label || payload[0].name;
    return (
      <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '10px' }}>
        <p style={{ fontWeight: 'bold', color: 'black' }}>{displayLabel}</p>
        {payload.map((entry, index) => (
          <p key={`item-${index}`} style={{ color: entry.color || entry.payload.fill, margin: '5px 0 0 0' }}>
            {`${entry.name}: ${entry.value.toLocaleString()}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};


/**
 * AnalyticsOverview Component
 * Displays an interactive chart for yearly medicine distribution with selectable categories.
 */
const AnalyticsOverview = ({ data }) => {
  const [activeCategory, setActiveCategory] = useState('eu_aut_status');
  const [activeBars, setActiveBars] = useState([]);
  const [hoveredBar, setHoveredBar] = useState(null);

  /**
   * Memoised calculation for the yearly distribution chart.
   * This groups data by authorisation year and the selected category.
   */
  const { yearlyChartData, categoryKeys } = useMemo(() => {
    if (!data) return { yearlyChartData: [], categoryKeys: [] };

    const yearlyData = {};
    const categorySet = new Set();

    data.forEach(product => {
      if (product.eu_aut_date && product[activeCategory]) {
        const year = product.eu_aut_date.split('-')[0];
        const category = activeCategory === 'eu_legal_basis' ? getEuLegalBasisDescription(product[activeCategory]) : product[activeCategory];

        categorySet.add(category);

        if (!yearlyData[year]) {
          yearlyData[year] = {};
        }
        yearlyData[year][category] = (yearlyData[year][category] || 0) + 1;
      }
    });

    const sortedYears = Object.keys(yearlyData).sort();
    const chartData = sortedYears.map(year => ({
      year,
      ...yearlyData[year]
    }));

    return { yearlyChartData: chartData, categoryKeys: Array.from(categorySet) };
  }, [data, activeCategory]);

  // Effect to reset activeBars whenever the category changes
  useEffect(() => {
    setActiveBars(categoryKeys);
  }, [categoryKeys]);


  // Handles legend item click to toggle bar visibility
  const handleLegendClick = (data) => {
    const { dataKey } = data;
    if (activeBars.includes(dataKey)) {
      setActiveBars(activeBars.filter(key => key !== dataKey));
    } else {
      setActiveBars([...activeBars, dataKey]);
    }
  };

  // Handles "Toggle All" button click
  const handleToggleAll = () => {
    if (activeBars.length === categoryKeys.length) {
      setActiveBars([]);
    } else {
      setActiveBars(categoryKeys);
    }
  };

  // Custom Legend component with hover and click functionality
  const renderLegend = (props) => {
    const { payload } = props;

    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {payload.map((entry, index) => {
            const isHidden = !activeBars.includes(entry.dataKey);
            const isHovered = hoveredBar === entry.dataKey;
            const opacity = isHovered || hoveredBar === null ? 1 : 0.4;
            const colour = isHidden ? '#ccc' : entry.color;

            return (
              <li
                key={`legend-item-${index}`}
                onClick={() => handleLegendClick(entry)}
                onMouseEnter={() => setHoveredBar(entry.dataKey)}
                onMouseLeave={() => setHoveredBar(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  opacity: isHidden ? 0.6 : 1,
                  transition: 'opacity 0.2s ease-in-out',
                  textDecoration: isHidden ? 'line-through' : 'none'
                }}
              >
                <span style={{
                  display: 'inline-block',
                  width: '10px',
                  height: '10px',
                  backgroundColor: colour,
                  marginRight: '5px'
                }} />
                <span style={{ color: 'var(--text-primary)' }}>{entry.value}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const totalMedicines = data ? data.length : 0;
  let chartCategoryName = '';
  switch (activeCategory) {
    case 'eu_legal_basis':
      chartCategoryName = 'Legal Basis for Marketing Authorisation';
      break;
    case 'atc_code_nameL1':
      chartCategoryName = 'ATC Level 1';
      break;
    case 'substance_type':
      chartCategoryName = 'Substance Types';
      break;
    case 'eu_od_at_marketing_bool':
      chartCategoryName = 'Orphan at Marketing Authorisation';
      break;
    case 'eu_aut_status':
      chartCategoryName = 'Current Authorisation Status';
      break;
    case 'eu_aut_type_initial':
      chartCategoryName = 'Authorisation Types at Marketing Authorisation';
      break;
    default:
      chartCategoryName = '';
  }

  return (
    <div className="med-content-container">
      <h1>Quick Overview of EU Approved Medicines</h1>
      <hr className="med-top-separator" />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
        {/* Yearly Distribution Chart */}
        <div>
          <div style={{ textAlign: 'center' }}>
            <p>Based on data from the <strong>EMRD</strong>, this interactive chart offers insights into the <strong>{totalMedicines}</strong> medicines that have been authorised in the EU over the years.</p>
          </div>

          {/* New Tab-like Selection for Categories */}
          <div className="analytics-tabs-container">
            <button
              className={`analytics-tab ${activeCategory === 'eu_aut_status' ? 'active-tab' : ''}`}
              onClick={() => setActiveCategory('eu_aut_status')}
            >
              Authorisation Status
            </button>
            <button
              className={`analytics-tab ${activeCategory === 'substance_type' ? 'active-tab' : ''}`}
              onClick={() => setActiveCategory('substance_type')}
            >
              Substance Types
            </button>
            <button
              className={`analytics-tab ${activeCategory === 'atc_code_nameL1' ? 'active-tab' : ''}`}
              onClick={() => setActiveCategory('atc_code_nameL1')}
            >
              ATC Level 1
            </button>
            <button
              className={`analytics-tab ${activeCategory === 'eu_legal_basis' ? 'active-tab' : ''}`}
              onClick={() => setActiveCategory('eu_legal_basis')}
            >
              Legal Basis
            </button>
            <button
              className={`analytics-tab ${activeCategory === 'eu_od_at_marketing_bool' ? 'active-tab' : ''}`}
              onClick={() => setActiveCategory('eu_od_at_marketing_bool')}
            >
              Orphan Drugs
            </button>
            <button
              className={`analytics-tab ${activeCategory === 'eu_aut_type_initial' ? 'active-tab' : ''}`}
              onClick={() => setActiveCategory('eu_aut_type_initial')}
            >
              Authorisation Types
            </button>
          </div>
          {/* End of New Tab-like Selection */}

          <div style={{ height: '400px', width: '100%' }}>
            <h3 style={{ textAlign: 'center', color: 'var(--text-primary)' }}>{chartCategoryName} for EU approved medicines by Authorisation year</h3>
            <div className="chart-container-overview">
              <div className="watermark">emrd.uu.nl</div>
              <ResponsiveContainer>
                <BarChart data={yearlyChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="year"
                    label={{ value: 'Approval year', position: 'insideBottom', offset: -10 }}
                    // Force display of all years
                    ticks={yearlyChartData.map(d => d.year)}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend content={renderLegend} wrapperStyle={{ cursor: 'pointer', textAlign: 'center', paddingTop: '20px' }} />
                  {categoryKeys.map((key, index) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="a"
                      fill={COLOURS[index % COLOURS.length]}
                      style={{
                        opacity: hoveredBar && key !== hoveredBar ? 0.4 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                      }}
                      hide={!activeBars.includes(key)}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ paddingTop: '20px', display: 'flex', justifyContent: 'center' }}>
            <button onClick={handleToggleAll} className="med-primary-solid med-bx-button">
              {activeBars.length === categoryKeys.length ? 'Untoggle All' : 'Toggle All'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsOverview;